<template>
  <header
    class="m-navbar"
    :class="{
      [`m-navbar--${layout}`]: layout,
      'm-navbar--headroom': headroom,
      'm-navbar--headroom-active': headroom && headroomActive,
      'm-navbar--overlay': overlay,
      'm-navbar--overlay-active': overlay && overlayMode,
    }"
  >
    <div class="m-navbar__bar" :style="overlay ? overlayStyle : null">
      <MContainer class="m-navbar__container">
        <slot />
      </MContainer>
    </div>
  </header>
</template>

<script>
import { deprecate } from '../../../lib/console'
import MContainer from '../../layout/Container/MContainer'
/**
 * The top navbar for the whole site
 */
export default {
  name: 'navbar',
  components: { MContainer },
  props: {
    /**
     * - Static: standard layout that scrolls with the page
     * - Fixed: navbar is fixed at the top and does not take up vertical space
     * - Sticky: navbar is fixed at the top, but takes up vertical space
     */
    layout: {
      type: String,
      required: false,
      default: 'fixed',
      validator: (prop) => ['static', 'fixed', 'sticky'].includes(prop),
    },
    /**
     * Hide the navbar away when the user scrolls
     * to give more room for the content
     */
    headroom: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Transparent navbar until the user scrolls
     */
    overlay: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      headroomActive: false,
      overlayMode: true,
      overlayStyle: null,
    }
  },
  created() {
    if (this.$options.props.layout) {
      deprecate({
        name: this.$options.name, // component name
        prop: 'layout',
        replacement: 'header slot in MApp wrapper',
        removalDate: '2025-04-14',
        ticket: 'https://app.clickup.com/t/2410881/ENG-29098',
      })
    }
  },
  mounted() {
    if (window) {
      window.addEventListener('scroll', this.scrollHandler)
    }
  },
  beforeUnmount() {
    if (window) {
      window.removeEventListener('scroll', this.scrollHandler)
    }
  },
  methods: {
    scrollHandler() {
      this.triggerHeadroom()
      this.triggerOverlay()
    },
    triggerHeadroom() {
      if (this.headroom) {
        const currentScrollPosition =
          window.pageYOffset || document.documentElement.scrollTop
        // If the difference between the current scroll position and the last
        // scroll position is less than a 50px offset, stop here
        if (
          currentScrollPosition < 0 ||
          Math.abs(currentScrollPosition - this.lastScrollPosition) < 50
        ) {
          return
        }

        this.headroomActive = !(currentScrollPosition < this.lastScrollPosition)

        // Set the current scroll position as the last scroll position
        this.lastScrollPosition = currentScrollPosition
      } else if (this.headroomActive) {
        this.headroomActive = false
      }
    },
    triggerOverlay() {
      if (this.overlay) {
        // Adapted from:
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollY
        const supportPageOffset = window.pageXOffset !== undefined
        const isCSS1Compat = (document.compatMode || '') === 'CSS1Compat'
        const y = supportPageOffset
          ? window.pageYOffset
          : isCSS1Compat
            ? document.documentElement.scrollTop
            : document.body.scrollTop

        const revealPoint = 160
        const level = (y * 100) / revealPoint / 100

        if (level > 0.75) {
          this.overlayMode = false
        } else {
          this.overlayMode = true
        }

        this.overlayStyle = `background-color:rgba(255,255,255,${level});`
      } else if (this.overlayStyle) {
        this.overlayStyle = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/breakpoint';

.m-navbar {
  left: 0;
  top: 0;
  width: 100%;
  z-index: var(--z-index-header);

  &__bar {
    align-items: center;
    background-color: var(--skin-background-light);
    box-shadow: 0 1px 4px 0
      color-mix(in sRGB, var(--color-grey-700), transparent 80%);
    display: flex;
    height: var(--header-height);
    transition-duration: var(--animation-speed);
    transition-property: background-color, transform;
    transition-timing-function: ease-out;
  }

  &__container {
    display: flex;
    height: 100%;
  }

  // Stuck at the top, doesn't take up room, always there
  &--fixed {
    position: fixed;
  }

  // Normal DOM flow
  &--static {
    position: relative;
  }

  // Stuck at the top, takes up room, always there
  &--sticky {
    position: sticky;
  }

  // Hide on scroll, works with fixed or sticky
  &--headroom {
    // Disable click events on this container
    // When used with headroom, the container
    // can take up room, but with the actual header
    // hidden we don't want it to block clicks
    // on the main app content
    pointer-events: none;

    .m-navbar__bar {
      // Allows click events that we have passed
      // through from the header container
      pointer-events: auto;
      transform: translate3d(0, 0, 0);
    }

    @include breakpoint.breakpoint(tablet-down) {
      &-active {
        .m-navbar__bar {
          box-shadow: none;
          transform: translate3d(0, -100%, 0);
        }
      }
    }
  }

  &--overlay {
    .m-navbar__bar {
      background-color: transparent;
    }

    &-active {
      .m-navbar__bar {
        box-shadow: none;
      }
    }
  }
}
</style>
